import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { formatDateTime, formatMoney, t } from '@aquabox/shared';
import { usePrintQueue } from '../api/print-queue';
import { useMe } from '../api/queries';
import { ChannelPill } from '../components/Pills';
import { PrintPdfButton } from '../components/PrintPdfButton';
import {
  PRINT_BATCH_MAX,
  PRINT_QUEUE_PAGE_SIZE,
  canPrint,
  slipPath,
  slipsPath,
} from '../lib/print-queue';

/**
 * Rota kapısı (`/yazdir`). Rol henüz yüklenmemişken yönlendirme YAPILMAZ (yer imi ya da
 * yenileme sonrası ilk çizimde `me` boştur); yalnız rol belli olunca karar verilir.
 */
export function PrintGate() {
  const me = useMe();
  if (me.isPending) {
    return <p style={{ padding: 32, color: 'var(--text-secondary)' }}>{t('common.loading')}</p>;
  }
  if (!canPrint(me.data?.role)) return <Navigate to="/" replace />;
  return <PrintQueue />;
}

/**
 * Yazdırma kuyruğu (K2c, rota `/yazdir`): panel kanallarından gelen, henüz basılmamış açık
 * siparişler. Seçilenler tek toplu fiş PDF'i olarak açılır; sıra ekrandaki sıradır.
 * "Basıldı" işareti yalnız bu tarayıcıdadır (`lib/print-queue.ts`).
 */
export function PrintQueue() {
  const queue = usePrintQueue(true);
  const [showPrinted, setShowPrinted] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  const visible = queue.queue.filter((o) => showPrinted || !queue.printed.has(o.id));
  // Seçim liste sırasıyla alınır: toplu PDF'te fişler ekranda görünen sırayla dizilir.
  const chosenIds = visible.filter((o) => selected.has(o.id)).map((o) => o.id);
  const overLimit = chosenIds.length > PRINT_BATCH_MAX;

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div style={{ padding: '24px 32px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p className="no-print" style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
        {t('panel.print.intro')}
      </p>

      <div
        className="no-print"
        style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}
      >
        <span style={{ fontWeight: 600, fontSize: 14 }}>
          {t('panel.print.unprintedCount', { n: queue.unprintedCount })}
        </span>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13.5 }}>
          <input
            type="checkbox"
            checked={showPrinted}
            onChange={(e) => setShowPrinted(e.target.checked)}
          />
          {t('panel.print.showPrinted')}
        </label>
        <span style={{ flex: 1 }} />
        <PrintPdfButton
          path={slipsPath(chosenIds)}
          label={t('panel.print.printSelected', { n: chosenIds.length })}
          disabled={chosenIds.length === 0 || overLimit}
          className="btn btn-primary"
          onOpened={() => {
            queue.markOrdersPrinted(chosenIds);
            setSelected(new Set());
          }}
        />
      </div>

      {overLimit ? (
        <p role="alert" className="no-print" style={{ fontSize: 13, color: 'var(--danger)' }}>
          {t('panel.print.tooMany', { max: PRINT_BATCH_MAX })}
        </p>
      ) : null}

      {queue.truncated ? (
        <p
          role="status"
          className="no-print"
          style={{ fontSize: 13, color: 'var(--text-secondary)' }}
        >
          {t('panel.print.truncated', { n: queue.total, max: PRINT_QUEUE_PAGE_SIZE })}
        </p>
      ) : null}

      {queue.isPending ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('common.loading')}</p>
      ) : queue.isError ? (
        <p style={{ color: 'var(--danger)' }}>{t('panel.print.loadError')}</p>
      ) : visible.length === 0 ? (
        <p style={{ color: 'var(--text-secondary)' }}>{t('panel.print.empty')}</p>
      ) : (
        <div className="card" style={{ overflow: 'auto' }}>
          <table
            className="print-table"
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}
          >
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th className="no-print" />
                <th>{t('panel.print.colTime')}</th>
                <th>{t('panel.print.colChannel')}</th>
                <th>{t('panel.print.colCustomer')}</th>
                <th>{t('panel.print.colAddress')}</th>
                <th style={{ textAlign: 'right' }}>{t('panel.print.colTotal')}</th>
                {showPrinted ? <th>{t('panel.print.colState')}</th> : null}
                <th className="no-print" />
              </tr>
            </thead>
            <tbody>
              {visible.map((o) => {
                const isPrinted = queue.printed.has(o.id);
                return (
                  <tr key={o.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="no-print">
                      <input
                        type="checkbox"
                        checked={selected.has(o.id)}
                        onChange={() => toggle(o.id)}
                        aria-label={t('panel.print.selectRow')}
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(o.receivedAt)}</td>
                    <td>
                      <ChannelPill channel={o.channel} source={o.source} />
                    </td>
                    <td>{o.customerName}</td>
                    <td>{o.address}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {formatMoney(o.totalAmount)}
                    </td>
                    {showPrinted ? (
                      <td>
                        {isPrinted ? (
                          <span className="pill" style={{ fontSize: 12, fontWeight: 600 }}>
                            {t('panel.print.printedBadge')}
                          </span>
                        ) : null}
                      </td>
                    ) : null}
                    <td className="no-print" style={{ textAlign: 'right' }}>
                      <PrintPdfButton
                        path={slipPath(o.id)}
                        label={t('panel.print.reprint')}
                        onOpened={() => queue.markOrdersPrinted([o.id])}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
